import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";

import Carousel from "../components/Carousel";
import EllipsisLoader from "../components/EllipsisLoader";
import FloorplanCustomiser from "../components/FloorplanCustomiser";
import FloorplanLightbox from "../components/FloorplanLightbox";
import FullscreenModal from "../components/FullscreenModal";
import HouseFilters from "../components/HouseFilters";
import HouseTypeSelector from "../components/HouseTypeSelector";
import Icon from "../components/Icon";
import SearchResultsBar from "../components/SearchResultsBar";
import Selectable from "../components/Selectable";
import SeriesListing from "../components/SeriesListing";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { getPageTitle } from "../resources/routes";
import { FloorplanOption,Home,HouseType,IconName,LandType,Series } from "../types";
import { scrollToTop,scrollToWithOffset } from "../util";

const ChooseFloorplanScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const location = useLocation();

  const windowSize = useWindowSize();

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [lightboxSvg, setLightboxSvg] = useState("");
  const [isLightboxShown, setIsLightboxShown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [carouselWidth, setCarouselWidth] = useState(100);
  const [hasSortChanged, setHasSortChanged] = useState(false);
  const [hasChosenSeries, setHasChosenSeries] = useState(false);
  const [selectedSeriesHomes, setSelectedSeriesHomes] = useState<Home[]|null>(null);

  const debouncedGetSeriesFromApi = useDebouncedCallback(() => {
    apiContext.resetHomeSearchPagination();
    apiContext.getSeries(undefined, hasSortChanged);
  }, 1000);

  const debouncedGetSeriesFromApiWithPage = useDebouncedCallback((page: number) => {
    apiContext.getSeries(page, hasSortChanged);
  }, 1000);

  useEffect(() => {
    scrollToTop();
    navContext.setFooterNavVisible(true);
    navContext.setNextButtonVisible(true);
    navContext.setLoginButtonVisible(false);
    navContext.setBackButtonVisible(false);
    navContext.setRestartButtonVisible(false);
    navContext.setNextButtonVisible(true);
    navContext.setNextBtnText("Next");
    navContext.resetNextBtn();
    
    navContext.setBackButtonVisible(false);
    navContext.setRestartButtonVisible(false);

    userContext.setSelectedFacade(null);
    userContext.resetSelectedFacadeFinishOption();

    navContext.setOnNavigateBack(() => {
      // Reset the form values when the user goes back
      apiContext.setSelectedSeries(null);
      apiContext.setHouseType(null);
      apiContext.resetHouseFilters();
      userContext.setSelectedHome(null);
      userContext.resetSelectedFloorplanOptions();
      userContext.resetIsFloorplanFlipped();
      navContext.setSubtotalVisible(false);
    });

    handleFooterNavVisibilty();

    return () => {
      // Unbind the handler when we unmount
      navContext.resetOnNavigateBack();
    };

  }, []);

  useEffect(() => {
    // When search filters change, debounce search for new homes
    if (apiContext.houseType !== null) {
      apiContext.setIsLoading(true);
      userContext.setSelectedHome(null);
      apiContext.setSelectedSeries(null);
      setCurrentSlideIndex(0);
      debouncedGetSeriesFromApi();
      setHasChosenSeries(false);
    }
  }, [
    apiContext.houseType,
    apiContext.numBeds,
    apiContext.numBaths,
    apiContext.numCars,
    apiContext.numLiving,
    apiContext.searchText,
    apiContext.lotWidth,
    apiContext.series,
    apiContext.sortMethod,
  ]);

  useEffect(() => {
    handleFooterNavVisibilty();
  }, [
    apiContext.houseType,
    userContext.selectedHome,
    apiContext.selectedSeries,
    hasChosenSeries,
    userContext.subtotalPrice,
  ]);

  useEffect(() => {
    if (apiContext.enteredViaHome && !userContext.selectedHome) {
      scrollToSearch();
      const existingHomeIndex = apiContext.homeSearchResultsWithPlaceholders
        .map((item) => (item ? item.id : -1))
        .indexOf(apiContext.enteredViaHome.id);
      if (existingHomeIndex > -1) {
        setCurrentSlideIndex(existingHomeIndex);

        userContext.setSelectedHome(
          apiContext.homeSearchResultsWithPlaceholders[existingHomeIndex]
        );

        // Now remove the entered via home link so they can choose other homes etc
        apiContext.setEnteredViaHome(null);
      }
    }
  }, [
    apiContext.homeSearchResultsWithPlaceholders,
    apiContext.enteredViaHome,
    userContext.selectedHome,
  ]);

  // If on mobile, we need to auto-select the currently viewed home when it loads
  /*useEffect(() => {
    if (windowSize.width <= 992) {
      if (
        apiContext.homeSearchResultsWithPlaceholders.length > currentSlideIndex
      ) {
        const targetHome =
          apiContext.homeSearchResultsWithPlaceholders[currentSlideIndex];
        if (targetHome !== null) {
          onClickHome(targetHome, currentSlideIndex);
        }
      }
    }
  }, [
    apiContext.homeSearchResultsWithPlaceholders,
    windowSize.width,
    currentSlideIndex,
  ]);*/

  useEffect(() => {
    if (windowSize.width <= 992 && apiContext.homeSearchResultsWithPlaceholders.length && apiContext.homeSearchResultsWithPlaceholders[0] !== null) {
      setTimeout(function () {
        // https://zimpledigital.teamwork.com/#/tasks/223303 - Scroll to results section when selecting a House Type
        scrollToWithOffset(".floorplans-container .pyht-carousel-inner", -50);
      }, 250);
    }
  }, [
    apiContext.homeSearchResultsWithPlaceholders,
    windowSize.width,
  ]);

  useEffect(() => {
    if (userContext.isKioskMode || windowSize.width <= 1080) {
      setCarouselWidth(100);
    } else {
      setCarouselWidth(77);
    }
  }, [userContext.isKioskMode, windowSize.width]);

  useEffect(() => {
    if(apiContext.selectedSeries && apiContext.selectedSeries.homes) {
      const $selectedTab = $(".pyht-selectable.selected .pyht-series-listing .tab.active");
      if($selectedTab.length){
        apiContext.selectedSeries.homes.map((home, index) => {
          if($selectedTab.data("id") == home.id){
            userContext.setSelectedHome(home);
          }
        });
      }
      setSelectedSeriesHomes(orderBySelectedHome(apiContext.selectedSeries.homes));
    }
  }, [apiContext.selectedSeries]);

  useEffect(() => {
    if(!hasChosenSeries && apiContext.selectedSeries && apiContext.selectedSeries.homes) {
      setSelectedSeriesHomes(orderBySelectedHome(apiContext.selectedSeries.homes));
    }
  }, [userContext.selectedHome]);
  
  const onLoadMore = () => {
    if(apiContext.homeSearchResultsWithPlaceholders.length){
      if(apiContext.homeSearchCurrentPage < apiContext.homeSearchLastPage){
        debouncedGetSeriesFromApiWithPage(apiContext.homeSearchCurrentPage + 1);
      }
    }
  };

  const handleFooterNavVisibilty = () => {
    if(!hasChosenSeries){
      navContext.setNextBtnOnClick(() => {
        setHasChosenSeries(true);
        setTimeout(function () {
          scrollToWithOffset(".floorplan-results", -50);
        }, 250);
      });
      navContext.setBackButtonVisible(false);
    }else{
      navContext.resetNextBtn();
    }
    
    if(hasChosenSeries){
      navContext.setNextButtonEnabled(userContext.selectedHome !== null);
      navContext.setBackButtonVisible(true);
      navContext.setBackBtnOnClick(() => {
        setHasChosenSeries(false);
        scrollToTop();
      });
    }else{
      navContext.setNextButtonEnabled(
        apiContext.houseType !== null && apiContext.selectedSeries !== null
      );
    }

    navContext.setSubtotalVisible(
      apiContext.houseType !== null &&
        userContext.selectedHome !== null &&
        userContext.subtotalPrice > 0
    );
  };

  const onSlideHome = (homeIndex: number) => {
    /*if (window.innerWidth <= 992) {
      // Mobile only
      if (apiContext.homeSearchResultsWithPlaceholders.length > homeIndex) {
        userContext.setSelectedHome(
          apiContext.homeSearchResultsWithPlaceholders[homeIndex]
        );
      } else {
        userContext.setSelectedHome(null);
      }
    }*/
    setCurrentSlideIndex(homeIndex);
  };

  const onSlideSeries = (seriesIndex: number) => {
    /*if (window.innerWidth <= 992) {
      // Mobile only
      if (apiContext.seriesSearchResultsWithPlaceholders.length > seriesIndex) {
        apiContext.setSelectedSeries(
          apiContext.seriesSearchResultsWithPlaceholders[seriesIndex]
        );
      } else {
        apiContext.setSelectedSeries(null);
      }
    }*/
    setCurrentSlideIndex(seriesIndex);
    getSeriesPageIfNeeded(seriesIndex);
  };

  const onClickHome = (home: Home, index: number) => {
    userContext.setSelectedHome(home);
    setCurrentSlideIndex(index);
  };

  const onClickSeries = (series: Series, index: number) => {
    apiContext.setSelectedSeries(series);
    setCurrentSlideIndex(index);
  };

  const onClickHouseType = (option: HouseType) => {
    apiContext.toggleHouseType(option);
  };

  const scrollToSearch = () => {
    if (windowSize.width <= 992) {
      setTimeout(function () {
        // https://zimpledigital.teamwork.com/#/tasks/223303 - Scroll to results section when selecting a House Type
        scrollToWithOffset(".house-type-section-label", -50);
      }, 250);
    }
  };

  const onClickFloorplanOption = (homeId: number, option: FloorplanOption) => {
    userContext.toggleSelectedFloorplanOption(homeId, option);
    handleFooterNavVisibilty();
  };

  const onFlipFloorplan = (homeId: number, isFlipped: boolean) => {
    userContext.setIsFloorplanFlipped(homeId, isFlipped);
  };

  const onClickShowLightbox = (svg: string) => {
    // The user can click the lightbox link on the next item in the carousel on desktop
    setLightboxSvg(svg);
    setIsLightboxShown(true);
  };

  const onClickMobilePrev = () => {
    if (currentSlideIndex > 0) {
      const index = currentSlideIndex - 1;
      getSeriesPageIfNeeded(index);
      setCurrentSlideIndex(index);

      const targetHome = apiContext.homeSearchResultsWithPlaceholders[index];
      userContext.setSelectedHome(targetHome);
    }
  };

  const onClickMobileNext = () => {
    if (currentSlideIndex < apiContext.homeSearchTotalResults - 1) {
      const index = currentSlideIndex + 1;
      getSeriesPageIfNeeded(index);
      setCurrentSlideIndex(index);
      const targetHome = apiContext.homeSearchResultsWithPlaceholders[index];
      userContext.setSelectedHome(targetHome);
    }
  };

  const getSeriesPageIfNeeded = (index: number) => {
    // Find the page that the item is on and if we need to load new results for that page
    const pageNum = Math.ceil((index + 1) / apiContext.homeSearchPageSize);
    if (pageNum > 1) {
      if (apiContext.homeSearchResultsPaged[pageNum] === undefined) {
        if(window.innerWidth >= 992){
          debouncedGetSeriesFromApiWithPage(pageNum);
        }else{
          apiContext.getSeries(pageNum, hasSortChanged);
        }
      }
    }
  };

  const onCloseSearchModal = () => {
    setIsModalOpen(false);
    setIsModalClosing(false);
  };

  const onChangeSort = () => {
    setHasSortChanged(true);
  };

  const orderBySelectedHome = (array: Home[]) => {
    if(userContext.selectedHome){
      //find the index of the element in the array
      const index = array.findIndex(({id}) => userContext.selectedHome ? id === userContext.selectedHome.id : -1);
      if(index !== -1) {
        //if the matching element is found, 
        const updatedData = [...array];
        //then remove that element and use `unshift`
        updatedData.unshift(...updatedData.splice(index, 1));
        return updatedData;
      }
    }
    //if the matching element is not found, then return the same array
    return array;
  };

  return (
    <div className={"screen screen-choose-floorplan"}>
      <div className={"container"}>
        <h1 className="process-title">{ apiContext.processSettings.title }</h1>
        <p className="copy">Find a home that complements your land and style with Domaine Homes.</p>
        <p className="copy">Choose a floor plan to price your new home today. We&apos;ll consider every element of your home from facade finish to final upgrade options. Let&apos;s get started.</p>
        {apiContext.processSteps.length && (
          <>
            <div className="steps">
              {apiContext.processSteps.map(
                (step, index) => {
                  return (
                    <React.Fragment key={index}>
                      <div className="divider"></div>
                      <div className="step">
                        <div className="step-circle">{ index + 1 }</div>
                        <div className="step-text">
                          <div className="step-title">{ step.title }</div>
                          <div className="copy">{ step.copy }</div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                }
              )}
            </div>
            {apiContext.processSettings.video_file && (
              <div className="video">
                <div className="video-inner">
                  <video controls playsInline>
                    <source src={ apiContext.processSettings.video_file } type="video/mp4"></source>
                  </video>
                </div>
              </div>
            )}
            {apiContext.processSettings.video_url && (
              <div className="video">
                <div className="video-inner">
                  <iframe src={ apiContext.processSettings.video_url }></iframe>
                </div>
              </div>
            )}
          </>
        )}
        <h2 className="h1 step-title"><strong className="number">Step 1:</strong> {getPageTitle(location.pathname)}</h2>
        <HouseTypeSelector
          onClick={onClickHouseType}
          selected={apiContext.houseType}
        />
      </div>
      {apiContext.houseType !== null ? (
        <div className={"container floorplans-container"}>
          {windowSize.width >= 992 ? (
            <div className={"filters-desktop"}>
              <p className={"section-label"}>Filter By</p>
              <HouseFilters
                beds={apiContext.numBeds}
                living={apiContext.numLiving}
                baths={apiContext.numBaths}
                cars={apiContext.numCars}
                searchText={apiContext.searchText}
                onClickBeds={(val) => apiContext.toggleNumBeds(val)}
                onClickLiving={(val) => apiContext.toggleNumLiving(val)}
                onClickBaths={(val) => apiContext.toggleNumBaths(val)}
                onClickCars={(val) => apiContext.toggleNumCars(val)}
                onSearchChange={(val) => apiContext.setSearchText(val)}
                onScreenKeyboard={userContext.isKioskMode}
                inlineLabels
              />
            </div>
          ) : (
            <div
              className={"btn btn-secondary filters-mobile"}
              onClick={() => setIsModalOpen(true)}
            >
              Refine Your Search
              <Icon name={IconName.CHEVRON_DOWN} />
            </div>
          )}
          <SearchResultsBar 
            landType={LandType.VACANT_BLOCK}
            onChangeSort={() => onChangeSort()}
          />
          {apiContext.isLoading && !apiContext.isLoadingNextPage ? (
            <div />
          ) : apiContext.homeSearchResultsWithPlaceholders.length > 0 ? (
            <div className={"floorplan-results"}>
              {apiContext.selectedSeries && hasChosenSeries ? (
                selectedSeriesHomes && (
                  <Carousel
                    onSlide={onSlideHome}
                    currentSlideIndex={currentSlideIndex}
                    numItems={selectedSeriesHomes.length}
                    itemWidthPerc={carouselWidth}
                    spacingPx={windowSize.width >= 992 ? 30 : 0}
                    items={selectedSeriesHomes.map(
                      (home, index) => {
                        return (
                          <div key={index} onClick={() => onClickHome(home, index)} data-id={home.id}>
                            <Selectable
                              selected={
                                userContext.selectedHome !== null &&
                                userContext.selectedHome.id === home.id
                              }
                              className={`floorplan-${home.house_type}`}
                            >
                              <FloorplanCustomiser
                                home={home}
                                isActive={
                                  userContext.selectedHome !== null &&
                                  userContext.selectedHome.id === home.id
                                }
                                isFloorplanFlipped={
                                  userContext.isFloorplanFlipped.get(home.id) ||
                                  false
                                }
                                onFlipFloorplan={(isFlipped) =>
                                  onFlipFloorplan(home.id, isFlipped)
                                }
                                selectedOptions={
                                  userContext.selectedFloorplanOptions.get(
                                    home.id
                                  ) || []
                                }
                                onClickShowLightbox={onClickShowLightbox}
                                onClickOption={(floorplanOption) =>
                                  onClickFloorplanOption(
                                    home.id,
                                    floorplanOption as FloorplanOption
                                  )
                                }
                                onClickMobilePrev={onClickMobilePrev}
                                onClickMobileNext={onClickMobileNext}
                                floorplanNumber={index + 1}
                                totalFloorplans={apiContext.selectedSeries && apiContext.selectedSeries.homes ? apiContext.selectedSeries.homes.length : 0}
                              />
                            </Selectable>
                          </div>
                        );
                      }
                    )}
                  />
                )
              ) : (
                <>
                  <Carousel
                    onSlide={onSlideSeries}
                    currentSlideIndex={currentSlideIndex}
                    numItems={apiContext.homeSearchTotalResults}
                    itemWidthPerc={carouselWidth}
                    spacingPx={windowSize.width >= 992 ? 30 : 0}
                    items={apiContext.seriesSearchResultsWithPlaceholders.map(
                      (series, index) => {
                        if (series == null) {
                          return (
                            <div key={index}>
                              <Selectable
                                selected={false}
                              >
                                <div className={"floorplan-loading"}>
                                  <EllipsisLoader />
                                </div>
                              </Selectable>
                            </div>
                          );
                        }

                        return (
                          <div key={index} onClick={() => onClickSeries(series, index)}>
                            <Selectable
                              selected={
                                apiContext.selectedSeries !== null &&
                                apiContext.selectedSeries.id === series.id
                              }
                            >
                              <SeriesListing
                                series={series}
                                isActive={
                                  apiContext.selectedSeries !== null &&
                                  apiContext.selectedSeries.id === series.id
                                }
                              />
                            </Selectable>
                          </div>
                        );
                      }
                    )}
                  />
                  {apiContext.isLoadingNextPage ? (
                    <div className="load-more-indicator">
                      <EllipsisLoader />
                    </div>
                  ) : apiContext.homeSearchCurrentPage < apiContext.homeSearchLastPage && (
                    <div className="load-more-button">
                      <button className="btn" onClick={() => onLoadMore()}>Load More</button>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : windowSize.width <= 992 ? (
            <p className={"section-label no-results"}>
              No Homes Match Your Criteria
            </p>
          ) : null}
        </div>
      ): (
        <div className={"container section promo"}>
          {apiContext.chooseFloorplanSettings.promo_desktop_image && (
            <img
              className={"desktop"}
              src={apiContext.chooseFloorplanSettings.promo_desktop_image}
              alt={""}
            />
          )}
          {apiContext.chooseFloorplanSettings.promo_mobile_image && (
            <img
              className={"mobile"}
              src={apiContext.chooseFloorplanSettings.promo_mobile_image}
              alt={""}
            />
          )}
        </div>
      )}
      {windowSize.width <= 992 && isModalOpen && (
        <FullscreenModal
          title={"Refine Your Search"}
          onClose={onCloseSearchModal}
          closing={isModalClosing}
        >
          <div className={"container"}>
            <HouseFilters
              beds={apiContext.numBeds}
              living={apiContext.numLiving}
              baths={apiContext.numBaths}
              cars={apiContext.numCars}
              searchText={apiContext.searchText}
              onClickBeds={(val) => apiContext.toggleNumBeds(val)}
              onClickLiving={(val) => apiContext.toggleNumLiving(val)}
              onClickBaths={(val) => apiContext.toggleNumBaths(val)}
              onClickCars={(val) => apiContext.toggleNumCars(val)}
              onSearchChange={(val) => apiContext.setSearchText(val)}
              onScreenKeyboard={userContext.isKioskMode}
              inlineLabels
            />
            <button type="button" className="btn" onClick={() => onCloseSearchModal()}>Search</button>
          </div>
        </FullscreenModal>
      )}
      {isLightboxShown && userContext.selectedHome && (
        <FloorplanLightbox
          home={userContext.selectedHome}
          svg={lightboxSvg}
          selectedOptions={
            userContext.selectedFloorplanOptions.get(
              userContext.selectedHome.id
            ) || []
          }
          onClose={() => setIsLightboxShown(false)}
          isFloorplanFlipped={userContext.isFloorplanFlipped.get(userContext.selectedHome.id) ||false}
        />
      )}
      {userContext.isKioskMode && userContext.showKioskSplash && !userContext.hasKioskMenu && (
        <div className="splash" onClick={() => userContext.setShowKioskSplash(false)}>
          <img src={apiContext.kioskSplash} alt="Splash" />
        </div>
      )}
    </div>
  );
});

export default ChooseFloorplanScreen;
